/**
 * `?offer=TOKEN` on the funnel URL: the "finish setting up Pixley" email's
 * pay-link. Like a Shopify or Stripe checkout recovery link it lets whoever
 * holds it pay for the recipient's account, and nothing else: nobody is
 * signed in and no session is created. The token is signed by the backend
 * and names the account (core/funnel_abandoned.py); the page sends it to
 * POST /funnel/offer for the character to put on the stage, and with the
 * checkout, where the backend creates the session for the token's account.
 * It carries no price: every price on offer is one of the backend's fixed
 * stops, so a link can never name an amount. A tampered or expired token is
 * worth nothing and the funnel starts from the top.
 *
 * Kept for the funnel session (it has to survive a reload) and only ever
 * sent to our backend.
 */

export const OFFER_TOKEN_KEY = 'pixley_funnel_offer_token';
export const OFFER_SOURCE = 'finish_setup_email';
const OFFER_PARAM = 'offer';
// <base64url payload>.<hex hmac-sha256>
const TOKEN_RE = /^[A-Za-z0-9_-]{8,400}\.[0-9a-f]{64}$/;

export const normalizeOfferToken = (value) =>
  (typeof value === 'string' && TOKEN_RE.test(value.trim()) ? value.trim() : null);

/** Reads ?offer= from the landing URL (a fresh token replaces a stored one)
 *  and returns the token in force for this session, if any. */
export function captureOfferToken() {
  let fromUrl = null;
  try {
    fromUrl = normalizeOfferToken(new URLSearchParams(window.location.search).get(OFFER_PARAM));
  } catch (_) {
    // no window / bad URL
  }
  try {
    if (fromUrl) {
      sessionStorage.setItem(OFFER_TOKEN_KEY, fromUrl);
      return fromUrl;
    }
    return normalizeOfferToken(sessionStorage.getItem(OFFER_TOKEN_KEY));
  } catch (_) {
    return fromUrl;
  }
}

export const clearOfferToken = () => {
  try {
    sessionStorage.removeItem(OFFER_TOKEN_KEY);
  } catch (_) {
    // nothing stored
  }
};

// `?price=<cents>` beside the token: the price chip the parent tapped in the
// email. A whole number of cents or nothing; the page only ever uses it as
// the first month's chosen price, and the backend accepts it only if it is
// one of its own fixed stops (core/funnel_pwyw.py), like any chosen price.
const PRICE_PARAM = 'price';
export const captureOfferPrice = () => {
  try {
    const raw = new URLSearchParams(window.location.search).get(PRICE_PARAM);
    if (raw === null || !/^\d{2,5}$/.test(raw.trim())) return null;
    return Number(raw.trim());
  } catch (_) {
    return null;
  }
};
